import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Search, Filter, Eye, ChevronLeft, ChevronRight, CalendarIcon, Clock, Video, MessageSquare } from 'lucide-react';
import { getBookings } from '../lib/api';
import { toast } from 'sonner';
import { format, parseISO, isAfter, isBefore, isValid } from 'date-fns';
import { cn } from '../lib/utils';

const ITEMS_PER_PAGE = 10;

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
  accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await getBookings();
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const matchesSearch = searchQuery === '' || 
        booking.studentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.teacherName?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
      
      let matchesDate = true;
      if (booking.date) {
        const bookingDate = parseISO(booking.date);
        if (isValid(bookingDate)) {
          if (dateFrom && isValid(dateFrom)) {
            matchesDate = matchesDate && (isAfter(bookingDate, dateFrom) || booking.date === format(dateFrom, 'yyyy-MM-dd'));
          }
          if (dateTo && isValid(dateTo)) {
            matchesDate = matchesDate && (isBefore(bookingDate, dateTo) || booking.date === format(dateTo, 'yyyy-MM-dd'));
          }
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [bookings, searchQuery, statusFilter, dateFrom, dateTo]);

  // Sort by latest
  const sortedBookings = useMemo(() => {
    return [...filteredBookings].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });
  }, [filteredBookings]);

  // Pagination
  const totalPages = Math.ceil(sortedBookings.length / ITEMS_PER_PAGE);
  const paginatedBookings = sortedBookings.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, dateFrom, dateTo]);

  const clearDateFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  return (
    <div data-testid="bookings-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Bookings
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage all session bookings on the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {sortedBookings.length} bookings
          </span>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="booking-search-input"
                placeholder="Search by student or teacher name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="booking-status-filter" className="w-full lg:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date From */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="booking-date-from"
                  variant="outline"
                  className={cn(
                    "w-full lg:w-40 justify-start text-left font-normal",
                    !dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Date To */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="booking-date-to"
                  variant="outline"
                  className={cn(
                    "w-full lg:w-40 justify-start text-left font-normal",
                    !dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                onClick={clearDateFilters}
                className="text-slate-500"
              >
                Clear dates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                <TableHead className="font-semibold">Student</TableHead>
                <TableHead className="font-semibold">Teacher</TableHead>
                <TableHead className="font-semibold">Date & Time</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Price</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                    No bookings found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBookings.map((booking) => (
                  <TableRow 
                    key={booking.id} 
                    data-testid={`booking-row-${booking.id}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-medium text-sm">
                          {booking.studentName?.split(' ').map(n => n[0]).join('') || '?'}
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {booking.studentName || 'Unknown'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-medium text-sm">
                          {booking.teacherName?.split(' ').map(n => n[0]).join('') || '?'}
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {booking.teacherName || 'Unknown'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-slate-900 dark:text-white">
                          {booking.date ? format(parseISO(booking.date), 'MMM d, yyyy') : '-'}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {booking.startTime || '-'} ({booking.duration || 0} min)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        {booking.sessionType === 'video' ? (
                          <><Video className="w-3 h-3" /> Video</>
                        ) : (
                          <><MessageSquare className="w-3 h-3" /> Chat</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">R{booking.price || 0}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[booking.status] || STATUS_COLORS.pending}>
                        {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`booking-view-${booking.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, sortedBookings.length)} of {sortedBookings.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="bookings-prev-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400 px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                data-testid="bookings-next-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Booking Details</DialogTitle>
            <DialogDescription>
              View session booking information
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <Badge className={cn("text-sm py-1 px-3", STATUS_COLORS[selectedBooking.status])}>
                  {selectedBooking.status?.charAt(0).toUpperCase() + selectedBooking.status?.slice(1)}
                </Badge>
                <Badge variant="outline" className="flex items-center gap-1">
                  {selectedBooking.sessionType === 'video' ? (
                    <><Video className="w-3 h-3" /> Video Session</>
                  ) : (
                    <><MessageSquare className="w-3 h-3" /> Chat Session</>
                  )}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Student</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedBooking.studentName}</p>
                  <p className="text-xs text-slate-500">{selectedBooking.studentEmail}</p>
                </div>
                <div>
                  <span className="text-slate-500">Teacher</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedBooking.teacherName}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date</span>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {selectedBooking.date ? format(parseISO(selectedBooking.date), 'MMMM d, yyyy') : '-'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Time</span>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {selectedBooking.startTime} ({selectedBooking.duration} min)
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Price</span>
                  <p className="font-medium text-emerald-600 dark:text-emerald-400">
                    R{selectedBooking.price}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Payment Status</span>
                  <p className="font-medium text-slate-900 dark:text-white capitalize">
                    {selectedBooking.paymentStatus || 'Pending'}
                  </p>
                </div>
                {selectedBooking.subject && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Subject</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedBooking.subject}</p>
                  </div>
                )}
                {selectedBooking.notes && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Notes</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedBooking.notes}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-slate-500">Created</span>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {selectedBooking.createdAt 
                      ? format(new Date(selectedBooking.createdAt), 'MMMM d, yyyy HH:mm')
                      : '-'
                    }
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setSelectedBooking(null)}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
